import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LegalLinks } from "@/pages/Legal";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { afterLoginPath, useAuth } from "@/context/AuthContext";
import { Lockup, PublicLayout } from "@/components/brand/Brand";
import { Field } from "@/components/common";

export function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} className="q-btn q-btn-secondary w-full" data-testid="google-signin-btn">
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 38.2 44 33 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
      Continue with Google
    </button>
  );
}

export function AuthCard({ title, subtitle, children }) {
  return (
    <div className="w-full max-w-md q-rise">
      <div className="mb-8"><Lockup size="sm" tm /></div>
      <div className="q-card">
        <h1 className="font-display text-2xl font-extrabold text-q-text">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-q-muted">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

export default function SignIn() {
  const { session, signIn } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(loc.state?.error || "");
  const [busy, setBusy] = useState(false);

  if (session && session.type === "adult") return <Navigate to={afterLoginPath(session)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      nav(afterLoginPath(signIn(data)), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <AuthCard title="Sign in" subtitle="Welcome back to your family hub.">
        <form onSubmit={submit} className="space-y-4" data-testid="signin-form">
          <Field label="Email" htmlFor="si-email">
            <input id="si-email" type="email" autoComplete="email" className="q-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signin-email-input" />
          </Field>
          <Field label="Password" htmlFor="si-pass">
            <input id="si-pass" type="password" autoComplete="current-password" className="q-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signin-password-input" />
          </Field>
          <div className="-mt-2 text-right"><Link to="/forgot-password" className="text-xs font-semibold text-q-muted hover:text-q-text" data-testid="signin-forgot-link">Forgot password?</Link></div>
          {error && <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="signin-error">{error}</p>}
          <button className="q-btn q-btn-primary w-full" disabled={busy} data-testid="signin-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
        </form>
        <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-q-faint">
          <span className="h-px flex-1 bg-q-line" /> or <span className="h-px flex-1 bg-q-line" />
        </div>
        <GoogleButton />
        <div className="mt-6 flex flex-wrap justify-between gap-2 text-sm">
          <Link to="/signup" className="text-q-accent hover:underline" data-testid="signin-to-signup-link">Create an account</Link>
          <Link to="/kid" className="font-semibold hover:underline" style={{ color: "rgb(var(--glow-kids))" }} data-testid="signin-kid-link">I'm a kid</Link>
        </div>
        <p className="mt-4 text-xs text-q-faint"><LegalLinks testid="signin-legal" /></p>
      </AuthCard>
    </PublicLayout>
  );
}
